'use client';

import {
  type ComponentPropsWithoutRef,
  type MutableRefObject,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { DrawerOrModal } from '../drawer-or-modal/drawer-or-modal';

/**
 * Options for the imperative drawer-or-modal.
 * References props directly from DrawerOrModal to avoid type duplication.
 */
export type ImperativeDrawerOrModalOptions = Pick<
  ComponentPropsWithoutRef<typeof DrawerOrModal>,
  'isAboveBreakpoint' | 'showCloseButton' | 'drawerProps' | 'modalProps'
>;

type ImperativeDrawerOrModalContextType = {
  openDrawerOrModal: (content: ReactNode, options?: ImperativeDrawerOrModalOptions) => void;
  closeDrawerOrModal: () => void;
  /** When true, user-initiated dismiss (backdrop, ESC, swipe, X) is blocked. */
  dismissLockRef: MutableRefObject<boolean>;
};

const ImperativeDrawerOrModalContext = createContext<
  ImperativeDrawerOrModalContextType | undefined
>(undefined);

/**
 * Provider for imperative drawer-or-modal management.
 * Renders a responsive DrawerOrModal (drawer on mobile, modal on desktop) controlled via hook.
 *
 * @example
 * ```tsx
 * <ImperativeDrawerOrModalProvider>
 *   <App />
 * </ImperativeDrawerOrModalProvider>
 * ```
 */
export const ImperativeDrawerOrModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<ReactNode | null>(null);
  const optionsRef = useRef<ImperativeDrawerOrModalOptions>({});
  const dismissLockRef = useRef(false);

  const openDrawerOrModal = useCallback(
    (content: ReactNode, options?: ImperativeDrawerOrModalOptions) => {
      contentRef.current = content;
      optionsRef.current = options || {};
      setIsOpen(true);
    },
    [],
  );

  const closeDrawerOrModal = useCallback(() => {
    dismissLockRef.current = false;
    setIsOpen(false);
    contentRef.current = null;
    optionsRef.current = {};
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open && dismissLockRef.current) return;
    setIsOpen(open);
  }, []);

  return (
    <ImperativeDrawerOrModalContext.Provider
      value={{ openDrawerOrModal, closeDrawerOrModal, dismissLockRef }}>
      {children}
      <DrawerOrModal
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        isAboveBreakpoint={optionsRef.current.isAboveBreakpoint}
        showCloseButton={optionsRef.current.showCloseButton}
        drawerProps={optionsRef.current.drawerProps}
        modalProps={{
          width: 'responsive-sm',
          height: 'auto-height',
          ...optionsRef.current.modalProps,
        }}>
        {contentRef.current}
      </DrawerOrModal>
    </ImperativeDrawerOrModalContext.Provider>
  );
};

export const useImperativeDrawerOrModal = () => {
  const context = useContext(ImperativeDrawerOrModalContext);
  if (!context) {
    throw new Error(
      'useImperativeDrawerOrModal must be used within ImperativeDrawerOrModalProvider',
    );
  }
  return context;
};
